import { lazy, Suspense } from "react";
import { PageMeta } from "@/components/PageMeta";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import InteractiveDemo from "@/components/InteractiveDemo";

// Lazy load below-the-fold components to improve LCP
const Features = lazy(() => import("@/components/Features"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const Pricing = lazy(() => import("@/components/Pricing"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const FAQ = lazy(() => import("@/components/FAQ"));
const CTA = lazy(() => import("@/components/CTA"));
const Footer = lazy(() => import("@/components/Footer"));
const BeforeAfterShowcase = lazy(() =>
  import("@/components/social/BeforeAfterShowcase").then((m) => ({ default: m.BeforeAfterShowcase })),
);
const PWAInstallPrompt = lazy(() =>
  import("@/components/pwa/PWAInstallPrompt").then((m) => ({ default: m.PWAInstallPrompt })),
);
const FeedbackWidget = lazy(() =>
  import("@/components/feedback/FeedbackWidget").then((m) => ({ default: m.FeedbackWidget })),
);

// Lazy loaded wrapper for LiveCounter components
const LiveCounterSection = lazy(() =>
  import("@/components/social/LiveCounter").then((m) => ({
    default: () => (
      <section className="py-8 border-y border-border/30 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-4">
            <m.LiveCounter />
            <m.SocialProofBar />
          </div>
        </div>
      </section>
    ),
  })),
);

// Minimal section loader
const SectionLoader = () => <div className="py-8 animate-pulse bg-muted/10" />;

const Index = () => {
  return (
    <main className="min-h-screen bg-background">
      <PageMeta
        title="EduGenix — AI-Powered Academic Project Generator"
        description="Generate complete, AI-detection-free dissertations, theses, and reports with smart templates, diagrams, and verifiable citations. 3 free downloads."
        path="/"
      />
      <Navbar />
      <Hero />

      {/* Interactive Demo Section */}
      <InteractiveDemo />

      {/* Live Counter & Social Proof */}
      <Suspense fallback={<SectionLoader />}>
        <LiveCounterSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <Features />
      </Suspense>

      {/* Before/After Showcase */}
      <Suspense fallback={<SectionLoader />}>
        <section className="py-16 bg-gradient-to-b from-background to-muted/20">
          <div className="container mx-auto px-4 max-w-4xl">
            <BeforeAfterShowcase />
          </div>
        </section>
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <HowItWorks />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <Pricing />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <Testimonials />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <FAQ />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <CTA />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <Footer />
      </Suspense>

      {/* PWA Install Prompt */}
      <Suspense fallback={null}>
        <PWAInstallPrompt />
      </Suspense>

      {/* Feedback Widget */}
      <Suspense fallback={null}>
        <FeedbackWidget />
      </Suspense>
    </main>
  );
};

export default Index;
